import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Loader2, ArrowRight, Flame, CheckCircle2, Clock } from "lucide-react";
import { api, fileUrl, formatINR, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

/** Boosted / Featured properties list — pulls from /me/feature-requests + /boost/my-orders */
export default function MyBoosts() {
    const [loading, setLoading] = useState(true);
    const [featureReqs, setFeatureReqs] = useState([]);
    const [boostOrders, setBoostOrders] = useState([]);
    const [props, setProps] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const [f, b, myProps] = await Promise.all([
                api.get("/me/feature-requests").then((r) => r.data || []).catch(() => []),
                api.get("/boost/my-orders").then((r) => r.data?.items || r.data || []).catch(() => []),
                api.get("/me/properties").then((r) => r.data?.items || r.data || []).catch(() => []),
            ]);
            setFeatureReqs(f);
            setBoostOrders(b);
            const map = {};
            myProps.forEach((p) => { map[p.id] = p; });
            setProps(map);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const activeBoosts = featureReqs.filter((r) => r.status === "approved_active");
    const pending      = featureReqs.filter((r) => r.status === "pending");
    const history      = [...featureReqs.filter((r) => !["approved_active", "pending"].includes(r.status)), ...boostOrders];

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="my-boosts-page">
            <MobileScreenHeader title="My Boosts" subtitle="Featured & promoted listings" testid="boosts-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Summary card */}
                    <div className="px-4 -mt-2">
                        <div
                            className="text-on-image rounded-2xl p-5 shadow-xl"
                            style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)", boxShadow: "0 15px 35px -12px rgba(0,15,35,0.55)" }}
                            data-testid="boosts-summary-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F5E5A5" }}>Active Boosts</div>
                                <Rocket className="w-5 h-5" style={{ color: "#F5E5A5" }} />
                            </div>
                            <div className="mt-2 font-heading font-extrabold text-white text-3xl" data-testid="boosts-active-count">
                                {activeBoosts.length}
                            </div>
                            <div className="mt-1 text-[11px] text-white/70">{pending.length} pending · {history.length} completed</div>
                            <Link
                                to="/my-properties"
                                data-testid="boosts-boost-more"
                                className="mt-4 w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[12px] font-extrabold shadow"
                                style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                            >
                                Boost a Property <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>

                    {/* Active boosts */}
                    {activeBoosts.length > 0 && (
                        <div className="mt-5 px-4">
                            <div className="text-[11px] uppercase tracking-widest font-extrabold text-emerald-700 mb-2 flex items-center gap-1">
                                <Flame className="w-3.5 h-3.5" /> Active
                            </div>
                            <div className="space-y-2" data-testid="boosts-active-list">
                                {activeBoosts.map((r) => <BoostRow key={r.id} r={r} prop={props[r.property_id]} status="active" />)}
                            </div>
                        </div>
                    )}

                    {/* Pending */}
                    {pending.length > 0 && (
                        <div className="mt-5 px-4">
                            <div className="text-[11px] uppercase tracking-widest font-extrabold text-amber-700 mb-2 flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" /> Pending Approval
                            </div>
                            <div className="space-y-2" data-testid="boosts-pending-list">
                                {pending.map((r) => <BoostRow key={r.id} r={r} prop={props[r.property_id]} status="pending" />)}
                            </div>
                        </div>
                    )}

                    {/* History */}
                    <div className="mt-5 px-4 pb-6">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">History</div>
                        {history.length === 0 ? (
                            <div className="rounded-2xl bg-white border border-[#002657]/10 p-6 text-center text-[#002657]/60 text-sm" data-testid="boosts-history-empty">
                                No completed boosts yet.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="boosts-history-list">
                                {history.map((r) => <BoostRow key={r.id || r._id} r={r} prop={props[r.property_id]} status={r.status} />)}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function BoostRow({ r, prop, status }) {
    const iconBg = status === "active" ? "bg-emerald-50 text-emerald-600" :
                   status === "pending" ? "bg-amber-50 text-amber-600" :
                   "bg-[#f6f7fb] text-[#8B6712]";
    const Icon = status === "active" ? CheckCircle2 : Clock;
    return (
        <div className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center gap-3" data-testid={`boost-item-${r.id}`}>
            <div className={`w-11 h-11 rounded-lg overflow-hidden ${iconBg} flex items-center justify-center`}>
                {prop?.images?.[0] ? (
                    <img src={fileUrl(prop.images[0])} alt={prop.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <Icon className="w-5 h-5" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-[#002657] line-clamp-1">
                    {prop?.title || `Property #${(r.property_id || "").slice(0, 8)}`}
                </div>
                <div className="text-[10px] text-[#002657]/55">
                    ₹{(r.amount_inr ?? r.payable_inr ?? r.amount ?? 0).toLocaleString("en-IN")} · {(r.created_at || "").slice(0, 10)} · {status}
                </div>
            </div>
            {r.property_id && (
                <Link to={`/properties/${r.property_id}`} className="text-[#8B6712] text-[11px] font-extrabold uppercase tracking-widest">
                    View
                </Link>
            )}
        </div>
    );
}
